// client/src/services/notificationService.ts

import { apiClient, PaginatedResponse } from './api';

// Types pour les notifications
export interface Notification {
  id: string;
  user_id: string;
  tenant_id: string;
  type: 'system' | 'mission' | 'vehicle' | 'driver' | 'maintenance' | 'alert' | 'info';
  title: string;
  message: string;
  description?: string;
  details?: string;
  data?: Record<string, any>;
  is_read: boolean;
  read?: boolean; // Alias for is_read
  read_at?: string;
  created_at: string;
  // Additional fields for SuperAdmin
  category?: 'system' | 'tenant' | 'user' | 'payment' | 'security';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  tenant_name?: string;
  action_required?: boolean;
}

export interface CreateNotificationData {
  type: 'info' | 'success' | 'warning' | 'error';
  category: 'system' | 'tenant' | 'user' | 'payment' | 'security';
  title: string;
  description: string;
  details?: string;
  tenant_id?: string;
  user_id?: string;
  action_required?: boolean;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  metadata?: Record<string, any>;
}

export interface NotificationFilters {
  page?: number;
  limit?: number;
  is_read?: boolean;
  type?: 'system' | 'mission' | 'vehicle' | 'driver' | 'maintenance' | 'alert' | 'info';
}

export interface NotificationPreferences {
  id?: string;
  user_id?: string;
  tenant_id?: string;
  email_enabled: boolean;
  email_frequency: 'immediate' | 'daily' | 'weekly';
  system_notifications: boolean;
  mission_notifications: boolean;
  vehicle_notifications: boolean;
  driver_notifications: boolean;
  maintenance_notifications: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface NotificationStats {
  total: number;
  unread: number;
  urgent: number;
  action_required: number;
  by_category: {
    system: number;
    tenant: number;
    user: number;
    payment: number;
    security: number;
  };
  by_type: {
    info: number;
    success: number;
    warning: number;
    error: number;
  };
}

export interface NotificationSettings {
  email_notifications: boolean;
  system_alerts: boolean;
  weekly_reports: boolean;
  marketing_emails: boolean;
  notification_frequency: 'immediate' | 'daily' | 'weekly';
  categories: {
    system: boolean;
    tenant: boolean;
    user: boolean;
    payment: boolean;
    security: boolean;
  };
}

/**
 * Service pour la gestion des notifications
 */
export class NotificationService {
  private readonly baseUrl = '/admin/notifications';

  /**
   * Récupérer la liste des notifications avec filtres et pagination
   */
  async getNotifications(filters: NotificationFilters = {}): Promise<PaginatedResponse<Notification>> {
    const url = apiClient.buildUrl(this.baseUrl, filters);
    return apiClient.get<PaginatedResponse<Notification>>(url);
  }

  /**
   * Récupérer le nombre de notifications non lues
   */
  async getUnreadCount(): Promise<{ unread_count: number; timestamp: string }> {
    return apiClient.get<{ unread_count: number; timestamp: string }>(
      `${this.baseUrl}/unread-count`
    );
  }

  /**
   * Marquer une notification comme lue
   */
  async markAsRead(id: string): Promise<{ message: string; notification: Notification }> {
    return apiClient.patch<{ message: string; notification: Notification }>(
      `${this.baseUrl}/${id}/read`
    );
  }

  /**
   * Marquer toutes les notifications comme lues
   */
  async markAllAsRead(): Promise<{ message: string; count: number }> {
    return apiClient.patch<{ message: string; count: number }>(
      `${this.baseUrl}/mark-all-read`
    );
  }

  /**
   * Supprimer une notification
   */
  async deleteNotification(id: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`${this.baseUrl}/${id}`);
  }

  /**
   * Récupérer les préférences de notification
   */
  async getPreferences(): Promise<NotificationPreferences> {
    return apiClient.get<NotificationPreferences>(`${this.baseUrl}/preferences`);
  }

  /**
   * Mettre à jour les préférences de notification
   */
  async updatePreferences(
    preferences: Partial<NotificationPreferences>
  ): Promise<{ message: string; preferences: NotificationPreferences }> {
    return apiClient.put<{ message: string; preferences: NotificationPreferences }>(
      `${this.baseUrl}/preferences`,
      preferences
    );
  }

  /**
   * Créer une nouvelle notification
   */
  async createNotification(data: {
    user_id?: string;
    type: string;
    title: string;
    message: string;
    data?: Record<string, any>;
  }): Promise<{ message: string; notification: Notification }> {
    return apiClient.post<{ message: string; notification: Notification }>(this.baseUrl, data);
  }

  /**
   * Récupérer les notifications récentes (alias pour getNotifications avec limit)
   */
  async getRecentNotifications(limit: number = 5): Promise<Notification[]> {
    const response = await this.getNotifications({ limit });
    return response.data;
  }

  /**
   * Récupérer les statistiques des notifications
   */
  async getNotificationStats(): Promise<NotificationStats> {
    // Simulate stats from existing data
    const notifications = await this.getNotifications({ limit: 100 });
    return {
      total: notifications.pagination.total,
      unread: notifications.data.filter((n) => !n.is_read).length,
      urgent: notifications.data.filter((n) => n.priority === 'urgent').length,
      action_required: notifications.data.filter((n) => n.action_required).length,
      by_category: {
        system: notifications.data.filter((n) => n.category === 'system').length,
        tenant: notifications.data.filter((n) => n.category === 'tenant').length,
        user: notifications.data.filter((n) => n.category === 'user').length,
        payment: notifications.data.filter((n) => n.category === 'payment').length,
        security: notifications.data.filter((n) => n.category === 'security').length,
      },
      by_type: {
        info: notifications.data.filter((n) => n.type === 'info').length,
        success: 0, // Not in current type
        warning: notifications.data.filter((n) => n.type === 'alert').length,
        error: 0, // Not in current type
      },
    };
  }
}

// Instance singleton du service
export const notificationService = new NotificationService();